import { useEffect, useCallback, useRef } from 'react'
import { Flex, MuiDatePicker, Text, Box } from '@pinchenterprisesnpm/friday-ui'
import { FormikProps } from 'formik'
import { INewDuplicateModalFormValues } from '@src/features/shift-post-v2/modals/new-duplicate-modal/new-duplicate-modal/new-duplicate-modal.form'
import { INewEditModalFormValues } from '@src/features/shift-post-v2/modals/new-edit-modal/new-edit-modal/new-edit-modal.form'
import { IScheduleSectionFormValues } from '@src/features/shift-post-v2/steps/shift-details-step/components/schedule-section/schedule-section.types'
import { CustomValidationError } from 'features/shift-post-v2/style'
import { TDay } from '@src/ui/components/Admin/Shifts/ShiftEditDuplicate/utils/types'
import { WeekdaySelector } from './components/weekday-selector'
import {
  generateRecurringDays,
  generateRecurringDaysIncremental,
} from './recurring-day-picker.utils'

const { DatePickerSingle } = MuiDatePicker

export interface IRecurringDayPickerProps {
  formik: FormikProps<
    INewDuplicateModalFormValues | INewEditModalFormValues | IScheduleSectionFormValues
  >
  isDisabled?: boolean
}

export const RecurringDayPicker = ({ formik, isDisabled }: IRecurringDayPickerProps) => {
  const { values, setFieldValue, errors } = formik

  const prevValuesRef: React.MutableRefObject<{
    multiday_day_start: Date | undefined
    multiday_day_end: Date | undefined
    selected_weekdays: string[]
  }> = useRef({
    multiday_day_start: undefined,
    multiday_day_end: undefined,
    selected_weekdays: [],
  })

  const handleStartDateChange = useCallback(
    (selectedDay: Date) => {
      setFieldValue('multiday_day_start', selectedDay)
    },
    [setFieldValue]
  )

  const handleEndDateChange = useCallback(
    (selectedDay: Date) => {
      setFieldValue('multiday_day_end', selectedDay)
    },
    [setFieldValue]
  )

  useEffect(() => {
    /**
     * We always refresh the days when the start or end date changes
     * or when the weekdays change
     */
    if (values.multiday_day_start && values.multiday_day_end) {
      let days: TDay[] = []
      if (
        prevValuesRef.current.multiday_day_start &&
        prevValuesRef.current.multiday_day_end &&
        prevValuesRef.current.selected_weekdays
      ) {
        // Incremental generation - change only the days that are affected by the change
        days = generateRecurringDaysIncremental(
          prevValuesRef.current.multiday_day_start,
          prevValuesRef.current.multiday_day_end,
          prevValuesRef.current.selected_weekdays,
          values.multiday_day_start,
          values.multiday_day_end,
          values.selected_weekdays,
          values
        )
      } else {
        // Full generation - generate all days in the range
        days = generateRecurringDays(
          values.multiday_day_start,
          values.multiday_day_end,
          values.selected_weekdays,
          values
        )
      }
      setFieldValue('days', days)
    }

    prevValuesRef.current = {
      multiday_day_start: values.multiday_day_start,
      multiday_day_end: values.multiday_day_end,
      selected_weekdays: values.selected_weekdays,
    }
    // Only re-render when the start, end date or weekdays change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.multiday_day_start, values.multiday_day_end, values.selected_weekdays])

  const handleStartDateReset = useCallback(() => {
    setFieldValue('multiday_day_start', undefined)
    setFieldValue('days', [])
  }, [setFieldValue])

  const handleEndDateReset = useCallback(() => {
    setFieldValue('multiday_day_end', undefined)
    setFieldValue('days', [])
  }, [setFieldValue])

  const handleWeekdayToggle = useCallback(
    (day: string) => {
      const newSelectedWeekdays = values.selected_weekdays.includes(day)
        ? values.selected_weekdays.filter((d: string) => d !== day)
        : [...values.selected_weekdays, day]

      setFieldValue('selected_weekdays', newSelectedWeekdays)
      // Regenerate days if we have a date range selected
      if (values.multiday_day_start && values.multiday_day_end) {
        let days: TDay[] = []
        if (
          prevValuesRef.current.multiday_day_start &&
          prevValuesRef.current.multiday_day_end &&
          prevValuesRef.current.selected_weekdays
        ) {
          // Incremental generation - change only the days that are affected by the change
          days = generateRecurringDaysIncremental(
            prevValuesRef.current.multiday_day_start,
            prevValuesRef.current.multiday_day_end,
            prevValuesRef.current.selected_weekdays,
            values.multiday_day_start,
            values.multiday_day_end,
            newSelectedWeekdays,
            values
          )
        } else {
          // Full generation - generate all days in the range
          days = generateRecurringDays(
            values.multiday_day_start,
            values.multiday_day_end,
            newSelectedWeekdays,
            values
          )
        }
        setFieldValue('days', days)
      }

      prevValuesRef.current = {
        multiday_day_start: values.multiday_day_start,
        multiday_day_end: values.multiday_day_end,
        selected_weekdays: newSelectedWeekdays,
      }
    },
    [setFieldValue, values]
  )

  return (
    <Flex flexDirection='column'>
      <Flex gap='md'>
        <Box width='50%'>
          <Text mb='xs' mt={0}>
            Start Date
          </Text>
          <DatePickerSingle
            selectedDay={values.multiday_day_start}
            onDayClick={handleStartDateChange}
            onReset={handleStartDateReset}
            isDisabled={isDisabled}
          />
          {errors.multiday_day_start && typeof errors.multiday_day_start === 'string' ? (
            <CustomValidationError mt='xs' mb={0}>
              {errors.multiday_day_start}
            </CustomValidationError>
          ) : null}
        </Box>

        <Box width='50%'>
          <Text mb='xs' mt={0}>
            End Date
          </Text>
          <DatePickerSingle
            selectedDay={values.multiday_day_end}
            onDayClick={handleEndDateChange}
            onReset={handleEndDateReset}
            disabledDays={(date: Date) => {
              return values.multiday_day_start ? date < values.multiday_day_start : false
            }}
            isDisabled={isDisabled}
          />
          {errors.multiday_day_end && typeof errors.multiday_day_end === 'string' ? (
            <CustomValidationError mt='xs' mb={0}>
              {errors.multiday_day_end}
            </CustomValidationError>
          ) : null}
        </Box>
      </Flex>

      <Box mt='md'>
        <Text mb='xs'>Select Weekdays</Text>
        <WeekdaySelector
          selectedDays={values.selected_weekdays}
          onDayToggle={handleWeekdayToggle}
          error={Array.isArray(errors.selected_weekdays) ? errors.selected_weekdays : undefined}
        />
        {errors.days && typeof errors.days === 'string' && (
          <CustomValidationError mt='xs' mb={0}>
            {errors.days}
          </CustomValidationError>
        )}
      </Box>
    </Flex>
  )
}
